import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { allowedSendersReasons, mobxInjectSelect } from 'common/utils';
import propTypes from 'common/propTypes';
import { MessageForm } from 'common/components/MessageForm';

class ExistingConvMessageForm extends Component {
  static propTypes = {
    allowDebugAllowedSenders: PropTypes.bool,
    clearNewMessagesIndicator: PropTypes.func.isRequired,
    currentConversation: propTypes.conversation,
    markConversationAsRead: PropTypes.func.isRequired,
    openModal: PropTypes.func.isRequired,
    senderId: PropTypes.string,
    sendMessageToConversation: PropTypes.func,
    startTyping: PropTypes.func.isRequired,
  };

  _lastAllowedSendersReason = null;
  _lastConversation = null;

  componentDidUpdate() {
    this._displayAllowedSenderModalMaybe();
  }

  render() {
    const { allowDebugAllowedSenders, currentConversation, isProviderNetwork } = this.props;
    const { allowedSenders, counterParty, organization } = currentConversation || {};
    if (!counterParty) return null;

    const { p2pRecipient, patientDetails } = counterParty;

    let alwaysEscalate = false;
    if (p2pRecipient && p2pRecipient.escalationPolicy) {
      const { escalationPolicy } = p2pRecipient;
      alwaysEscalate = escalationPolicy.alwaysEscalate;
    }

    let disableMessageForm = false;
    if (patientDetails && patientDetails.smsOptedOut) {
      disableMessageForm = true;
    }

    if (allowDebugAllowedSenders && counterParty) {
      const hasAllowedSenders = allowedSenders && allowedSenders.length > 0;
      const isRoleConversation =
        counterParty.$entityType === 'user' && counterParty.p2pRecipientType === 'role';

      const shouldLog =
        isRoleConversation &&
        ((!hasAllowedSenders && !disableMessageForm) ||
          !currentConversation?.entitiesEnsured ||
          currentConversation?.allowedSendersReason);

      if (shouldLog) {
        Sentry.logger.warn('ExistingConvMessageForm: Potential allowedSenders issue', {
          conversationId: currentConversation?.id,
          allowedSendersCount: allowedSenders?.length || 0,
          allowedSendersReason: currentConversation?.allowedSendersReason,
          entitiesEnsured: currentConversation?.entitiesEnsured,
          counterPartyType: counterParty?.$entityType,
          p2pRecipientType: counterParty?.p2pRecipientType,
          isProviderNetwork,
          patientSmsOptedOut: patientDetails?.smsOptedOut,
          disableMessageForm,
        });
      }
    }

    return (
      <MessageForm
        allowedSenders={disableMessageForm ? [] : allowedSenders}
        alwaysEscalate={alwaysEscalate}
        onInputClick={this._onInputClick}
        onTyping={this._startTyping}
        organization={organization}
        sendMessage={this._sendMessage}
      />
    );
  }

  _displayAllowedSenderModalMaybe = () => {
    const { currentConversation, openModal } = this.props;
    const { allowedSendersReason } = currentConversation;
    const updateAllowed =
      this._lastAllowedSendersReason !== allowedSendersReason ||
      currentConversation !== this._lastConversation;

    if (updateAllowed && allowedSendersReasons[allowedSendersReason]) {
      openModal('allowedSenderModal', { reason: allowedSendersReason });
    }

    this._lastAllowedSendersReason = allowedSendersReason;
    this._lastConversation = currentConversation;
  };

  _onInputClick = () => {
    const { currentConversation, markConversationAsRead } = this.props;
    markConversationAsRead(currentConversation);
  };

  _startTyping = () => {
    const { currentConversation, senderId, startTyping } = this.props;
    startTyping(currentConversation.counterPartyId, { senderId });
  };

  _sendMessage = async (
    body,
    { attachmentFiles, escalate, membersMentionInConversation, priority } = {}
  ) => {
    const {
      clearNewMessagesIndicator,
      currentConversation,
      markConversationAsRead,
      senderId,
      sendMessageToConversation,
    } = this.props;
    const { organizationId } = currentConversation;

    await sendMessageToConversation(currentConversation.id, body, {
      attachmentFiles,
      escalate,
      organizationId,
      priority,
      membersMentionInConversation,
      senderId,
    });

    clearNewMessagesIndicator(currentConversation);
    await markConversationAsRead(currentConversation);
  };
}

export default mobxInjectSelect({
  composeMessageStore: ['senderId'],
  conversationStore: ['clearNewMessagesIndicator', 'markConversationAsRead'],
  messageStore: ['sendMessageToConversation'],
  messengerStore: ['isDebugAllowedSendersEnabled:allowDebugAllowedSenders'],
  modalStore: ['openModal'],
  typingStatusStore: ['startTyping'],
})(ExistingConvMessageForm);
